import React, { useEffect } from 'react';
import '../../documents/DocumentsListView.css';
import './ScribeListView.css';
import '../../list-view-page.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faTrash, faRepeat, faSpinner, faFolderOpen, faTimesCircle, faUpload } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  CustomDropdown,
  MAIN_TAB_PAGE_LABEL,
  medPromptBrowserTitle,
  ColumnCustomizationModal,
  ColumnCustomizationButton,
  RowContextMenu,
} from '@medprompt/shared';
import NewConsultationModal from '../NewConsultationModal';
import UploadConsultationModal from '../UploadConsultationModal';
import SearchBar from '../../detail/tabs/SearchBar';
import HelpButton from '../../help/HelpButton';
import { HELP_PAGE_IDS } from '../../help/helpContent';
import ScribeDeleteModal from './ScribeDeleteModal';
import ScribeRegenerateModal from './ScribeRegenerateModal';
import ScribeBulkDeleteModal from './ScribeBulkDeleteModal';
import ScribeBulkRegenerateModal from './ScribeBulkRegenerateModal';
import useScribeList, { ScribeViewMode } from './useScribeList';
import useScribeRecordingStart from './useScribeRecordingStart';
import useScribeRowActions from './useScribeRowActions';
import useScribeTable from './useScribeTable';
import useRowPatientNames from '../../patients/useRowPatientNames';
import '@medprompt/shared/src/common/modals.css';
import '@medprompt/shared/src/common/dropdowns.css';

const emptyMessageForViewMode = (viewMode) => {
  if (viewMode === ScribeViewMode.ARCHIVE) {
    return 'No archived consultations';
  }
  if (viewMode === ScribeViewMode.ALL_CONSULTATIONS) {
    return 'No active consultations';
  }
  return 'No consultations assigned to you';
};

const ScribeListViewController = () => {
  const navigate = useNavigate();
  const list = useScribeList();
  const patientNames = useRowPatientNames({ service: list.consultationService, idKey: 'consultationId' });
  const recordingStart = useScribeRecordingStart({
    consultationService: list.consultationService,
    user: list.user,
    navigate,
  });
  const rowActions = useScribeRowActions({
    consultationService: list.consultationService,
    hasConsultWrite: list.hasConsultWrite,
  });
  const table = useScribeTable({ list, patientNames, rowActions });

  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.scribe);
  }, []);

  const { hasConsultReadAll, hasConsultWrite, isSearchActive, searchResults, searchQuery } = list;
  const contextRow = rowActions.rowContextMenu.row;

  return (
    <div className="scribe-list-view list-view-page">
      <div className="header">
        <div className="header-title-text">
          <div className="header-title-with-help">
            <h1 className="tab-page-title">{MAIN_TAB_PAGE_LABEL.scribe}</h1>
            <HelpButton pageId={HELP_PAGE_IDS.scribeList} />
          </div>
          <CustomDropdown
            label="View Mode"
            selected={list.currentViewModeName}
            selectedIcon={list.currentViewModeIcon}
            options={list.viewModeOptions}
            isOpen={list.viewModeDropdown.isOpen}
            toggleDropdown={() => list.viewModeDropdown.toggle([])}
            onSelect={list.selectViewMode}
            containerRef={list.viewModeDropdown.dropdownRef}
            width="180px"
          />
        </div>
        <div className="header-buttons">
          {hasConsultReadAll && isSearchActive && (
            <button type="button" className="header-button clear-search-button" onClick={list.handleClearSearch}>
              <FontAwesomeIcon icon={faTimesCircle} /> Clear search
            </button>
          )}
          {hasConsultReadAll && (
            <SearchBar
              placeholder="Search consultations"
              onSearch={list.handleConsultationSearch}
              onResults={undefined}
              className="documents-list-search-bar"
              clearSearch={list.setClearSearchBarFunction}
            />
          )}
          <input
            type="file"
            id="audio-upload"
            accept="audio/*"
            style={{ display: 'none' }}
            onChange={recordingStart.handleFileUpload}
          />
          {hasConsultWrite && (
            <>
              <button
                className="header-button header-button-primary"
                onClick={recordingStart.handleNewRecordingClick}
                disabled={recordingStart.isStarting}
              >
                {recordingStart.isStarting ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMicrophone} /> New Recording
                  </>
                )}
              </button>
              <button
                className="header-button"
                onClick={() => document.getElementById('audio-upload').click()}
                disabled={recordingStart.isStarting}
              >
                <FontAwesomeIcon icon={faUpload} /> Upload
              </button>
            </>
          )}
        </div>
      </div>

      {hasConsultReadAll && isSearchActive && (
        <div className="documents-search-results-info">
          Found {searchResults.length} consultation{searchResults.length !== 1 ? 's' : ''} matching &quot;{searchQuery}&quot;
        </div>
      )}

      <div className={`drop-zone ${recordingStart.isDragging ? 'active' : ''}`}>Drop audio file to upload</div>

      <Table
          id="ScribeListTable"
          data={list.visibleConsultations}
          columns={table.columns}
          columnOrder={table.columnOrder}
          hiddenColumns={table.hiddenColumnsSet}
          settingsButton={<ColumnCustomizationButton onClick={() => table.setColumnCustomizationOpen(true)} />}
          getRowKey={(row) => row.consultationId}
          sortState={table.sortState}
          onSortChange={table.setSortState}
          filterValues={table.filterValues}
          onFilterChange={table.setFilterValues}
          disabledFilters={table.disabledFilters}
          onDisabledFiltersChange={table.setDisabledFilters}
          paginationState={table.paginationState}
          onPaginationChange={table.setPaginationState}
          highlightedRows={table.highlightedRows}
          onHighlightChange={table.setHighlightedRows}
          enableSelection={true}
          bulkActions={table.bulkActions}
          selectedRows={table.selectedRows}
          onSelectionChange={table.setSelectedRows}
          onRowClick={table.openConsultation}
          onRowContextMenu={rowActions.handleRowContextMenu}
          onCurrentPageRowsChange={patientNames.handleCurrentPageRowsChange}
          onFilteredSortedDataChange={table.handleFilteredSortedDataChange}
          emptyMessage={
            <div className="no-documents-container">
              <FontAwesomeIcon icon={faFolderOpen} size="2x" style={{ marginBottom: '10px', opacity: 0.5 }} />
              <p>{isSearchActive ? `No consultations matching "${searchQuery}"` : emptyMessageForViewMode(list.viewMode)}</p>
            </div>
          }
        />

      <NewConsultationModal
        isOpen={recordingStart.newConsultationModalOpen}
        onClose={recordingStart.closeNewConsultationModal}
        onStart={recordingStart.handleStartRecording}
        loading={recordingStart.isStarting}
      />

      <UploadConsultationModal
        isOpen={recordingStart.uploadModalOpen}
        onClose={recordingStart.closeUploadModal}
        onUpload={recordingStart.handleUploadSubmit}
        loading={recordingStart.isStarting}
        fileName={recordingStart.uploadingFileName}
      />

      <ScribeDeleteModal
        isOpen={rowActions.deleteModalOpen}
        onClose={rowActions.closeDeleteModal}
        onConfirm={rowActions.confirmDelete}
        consultation={rowActions.consultationToDelete}
        patientName={rowActions.consultationToDelete ? table.getPatientName(rowActions.consultationToDelete.nhsId) : ''}
        loading={rowActions.deleteLoading}
      />

      <ScribeRegenerateModal
        isOpen={rowActions.regenerateModalOpen}
        onClose={rowActions.closeRegenerateModal}
        onConfirm={rowActions.confirmRegenerate}
        consultation={rowActions.consultationToRegenerate}
        loading={rowActions.regenerateLoading}
        getPatientName={table.getPatientName}
      />

      <ScribeBulkRegenerateModal
        isOpen={table.bulkRegenerateModalOpen}
        onClose={table.closeBulkModal}
        onConfirm={table.confirmBulkRegenerate}
        consultations={table.consultationsForBulkAction}
        loading={table.bulkRegenerateLoading}
        getPatientName={table.getPatientName}
      />

      <ScribeBulkDeleteModal
        isOpen={table.bulkDeleteModalOpen}
        onClose={table.closeBulkModal}
        onConfirm={table.confirmBulkDelete}
        consultations={table.consultationsForBulkAction}
        getPatientName={table.getPatientName}
      />

      <RowContextMenu
        open={rowActions.rowContextMenu.open}
        x={rowActions.rowContextMenu.x}
        y={rowActions.rowContextMenu.y}
        onClose={rowActions.closeRowContextMenu}
        items={!contextRow ? [] : [
          {
            key: 'regenerate',
            icon: faRepeat,
            label: 'Regenerate consultation',
            onClick: () => rowActions.handleRegenerateClick(contextRow),
            hidden: !rowActions.getRowActions(contextRow).canRegenerate,
          },
          {
            key: 'delete',
            icon: faTrash,
            label: 'Delete consultation',
            onClick: () => rowActions.handleDeleteClick(contextRow),
            hidden: !rowActions.getRowActions(contextRow).canDelete,
            danger: true,
          },
        ]}
      />
      <ColumnCustomizationModal
        isOpen={table.columnCustomizationOpen}
        onClose={() => table.setColumnCustomizationOpen(false)}
        columns={table.columns}
        currentOrder={table.columnOrder}
        hiddenColumns={table.hiddenColumnsSet}
        onSave={(order, hidden) => {
          table.saveSettings(order, Array.from(hidden));
          table.setColumnCustomizationOpen(false);
        }}
      />
    </div>
  );
};

export default ScribeListViewController;
